/*
 * ******************************************************
 * Copyright (c) 2022 Jenke & Rauschert GbR <info@aiandor.de> - All Rights Reserved
 *
 * This file is part of a software product owned by the Jenke & Rauschert GbR.
 * Unauthorized distribution of this file is strictly prohibited.
 * ******************************************************
 */

import {Button, Card, DatePicker, Empty, Input, Modal, Space, Switch, Table, Tag, theme, Tooltip} from "antd";
import {SearchOutlined, WarningTwoTone} from "@ant-design/icons";
import React, {ReactElement, useEffect, useState} from "react";
import {get} from "../core/request";
import {useHistory} from "react-router-dom";
import {ViewState} from "../states";
import 'dayjs/locale/de';
import {openDrawer} from "../reducers/drawers-reducer";
import {useAppDispatch} from "../store";
import {CustomButton} from "./buttons";
import dayjs from "dayjs";
import {IconSet, IconType} from "./icons";
import {token} from "../core/auth/token";


// utils
function infoModal(info: string) {

    if(info) {
        return (
            <Tooltip
                title={
                <p style={{
                    whiteSpace: "pre-wrap",
                }}>
                    {info}
                </p>
            }
                placement="top">
                <CustomButton
                    mode={"info"}
                    iconColor={"#1890ff"}
                    noBorder={true}
                    onClick={
                        (e) => {
                            e.stopPropagation();
                            Modal.info({
                                title: "Notiz",
                                content:
                                    <p style={{
                                        whiteSpace: "pre-wrap",
                                    }}>
                                        {info}
                                    </p>,
                                onOk: () => {},
                                maskClosable: true,
                            })
                        }
                    } />
            </Tooltip>

        )
    } else {
        return (
            <CustomButton
                mode={"info"}
                disabled={true}
                iconColor={"#8c8c8c"}
                noBorder={true}
                onClick={
                    (e) => {
                        e.stopPropagation();
                    }
                } />

        )
    }

}

type TodoType = "done" | "todo" | "upload" | "report"
type TodoIconProps = {
    icon: IconType;
    color: string;
    tooltip: string;
}

function TodoIcon(props: TodoIconProps) {

    return (
            <Tooltip title={props.tooltip}>
                <div style={{
                    display: "flex",
                    justifyContent: "center"
                }}>
                    <IconSet type={props.icon} className={props.color}  />
                </div>
            </Tooltip>
    )
}

function getTodoIcon(todoType : TodoType) {

    switch (todoType) {
        case "done":
            return <TodoIcon icon={"circle-check"} color={"todo-done"} tooltip={"Alles erledigt"} />
        case "upload":
            return <TodoIcon icon={"upload"} color={"todo-warning"} tooltip={"Bitte Daten hochladen"} />
        case "report":
            return <TodoIcon icon={"report"} color={"todo-warning"} tooltip={"Bitte Bericht erstellen"} />
        case "todo":
        default:
            return <TodoIcon icon={"note"} color={"todo-warning"} tooltip={"offene Aufgaben"} />
    }
}

function getSorter(type: string, dataIndex: string) {
    switch (type) {
        case "numerical":
            return ((a: any, b: any) => a[dataIndex] - b[dataIndex])
        case"alphabetical":
            return ((a: any, b: any) => String(a[dataIndex]).localeCompare(String(b[dataIndex])))
    }
}

function getRender(render: string) {
    switch (render) {
        case "tags":
            return (tags: string[]) => (
                <>
                    {tags.map(tag => (
                        <Tag key={tag}>
                            {tag}
                        </Tag>
                    ))}
                </>
            )
        case "info":
            return (info: string) => infoModal(info);
            //return (info: string) => <p>{info}</p>
        case "todo":
            return (todoType: string) => getTodoIcon(todoType as TodoType);
        default:
            return (content: any) => content
    }
}

function getFilter(column: any) {
    switch (column.render) {
        case "tags":
            return (value: any, record: any) => record[column.dataIndex].some((v: string) => v === value);

        case undefined:
        case null:
        default:
            return (value: any, record: any) => record[column.dataIndex] === value;
    }
}

// default table
interface ApiTableProps {
    id: string,
    url: string,
    url_params?: string,
    title?: ReactElement<any, any>,
    searchable?: boolean,
    actions?: any,
    actionWidth?: number,
    tableProps?: any,
    pagination?: boolean,
    clickable?: boolean | string,
    onRow?: any,
    size?: any,
    buttons?: ReactElement<any, any>
    reloadSignals: boolean[],
    storeFilters?: boolean,
    heightReducer?: number,
}

export function ApiTable(props: ApiTableProps) {
    const history = useHistory();

    const [apiData, setApiData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [state, setState] = useState(ViewState.loading);

    const heightReducer = props.heightReducer ? props.heightReducer : 48 + 34 + 65 + 55 + 24 + 70;
    const [tableHeight, setTableHeight] = useState(window.innerHeight - heightReducer);

    // resize table on change of windowSize
    window.addEventListener('resize', () =>
        setTableHeight(window.innerHeight - heightReducer)
    )

    useEffect(() => {
        let url = props.url + "/table";
        if (props.url_params) {
            url += "?" + props.url_params;
        }
        get(url)
            .done((response) => {
                    var tabledata = response.data.table
                    setApiData(tabledata.data);
                    setFilteredData(tabledata.data);
                    let columns = tabledata.columns;
                    for (let column of columns) {
                        // split title by linebreak
                        column.title = column.title.split("\n").map((item: string, key: number) => {
                            return <span key={key}>{item}<br/></span>
                        })
                        if (column.sorter) {
                            column.sorter = getSorter(column.sorter, column.dataIndex);
                        }

                        column.onFilter = getFilter(column);

                        if (column.render) {
                            column.render = getRender(column.render);
                        }
                    }
                    if (props.actions) {
                        columns = [{
                            title: "",
                            render: props.actions,
                            fixed: 'left',
                            align: 'center',
                            width: props.actionWidth? props.actionWidth : 50
                        }, ...columns];
                    }
                    loadFilters(columns);
                    setState(ViewState.success);
                }
            ).fail((resp) => setState(ViewState.failed))
            .fail((e) => console.error(e));
    }, [props.url, props.actions, props.id, ...props.reloadSignals]);

    function onChange(pagination: any, filters: any, sorter: any, extra: any) {
        let table = getStoredFilters(props.id);
        if (extra['action'] === "sort") {
            table['sorter'] = [sorter['field'], sorter['order']];
            storeFilters(props.id, table);
            loadFilters(columns);
        } else if (extra['action'] === "filter") {
            table['filters'] = filters;
            storeFilters(props.id, table);
            loadFilters(columns);
        }
    }

    function loadFilters(cols: any) {
        let table = getStoredFilters(props.id);
        for (let colum of cols) {
            // @ts-ignore
            if (colum.sorter) {
                if (table['sorter'][0] === colum.dataIndex) {
                    // @ts-ignore
                    colum.sortOrder = table['sorter'][1];
                } else {
                    colum.sortOrder = null;
                }
            }
            // @ts-ignore
            if (table['filters'].hasOwnProperty(colum.dataIndex)) {
                // @ts-ignore
                colum.filteredValue = table['filters'][colum.dataIndex];
            }
        }
        setColumns(cols);
    }

    function getStoredFilters(id: string) {
        if (!window.localStorage.tables) {
            window.localStorage.tables = JSON.stringify({});
        }
        let tables = JSON.parse(window.localStorage.tables);
        if (!props.storeFilters || !tables.hasOwnProperty(id)) {
            tables[id] = {
                "sorter": [undefined, undefined],
                "filters": {}
            };

            // filter inactive devices by default
            if (id === "Kunden_Geräte" || id === "Geräte") {
                tables[id]['filters'] = {
                    "status":["aktiv"]
                }
            }
        }
        return tables[id];
    }

    function storeFilters(id: string, table: {}) {
        let tables = JSON.parse(window.localStorage.tables);
        tables[id] = table;
        window.localStorage.tables = JSON.stringify(tables);
    }

    function getFooterContent() {
        // console.debug(getStoredFilters(props.id)['filters'])
        const filter = getStoredFilters(props.id)['filters']
        // itterate through dict and check if any filter is active
        for (var key in filter) {
            if (filter[key] != null) {
                return "Es sind Filter aktiv! Bei Fehlen von Daten, bitte die Filter überprüfen."
            }
        }
        return ""
    }

    function getSpinState() {
        switch (state) {
            case ViewState.loading:
                return true;
            case ViewState.success:
                return false;
            case ViewState.failed:
            default:
                return {
                    indicator: <>
                        <WarningTwoTone style={{fontSize: 48, paddingTop: 48}} twoToneColor='#ffc107'/>
                        <br/>
                        <span style={{color: '#6c757d'}}>Daten konnten nicht geladen werden!</span>
                    </>,
                }
        }
    }

    return (
        <Card
            headStyle={{backgroundColor: 'rgb(250, 250, 250)', borderBottom: 0}}
            bodyStyle={{padding: 0}}
            title={props.title}
            extra={<Space size={12}>
                {props.searchable ? (
                    <Input
                        placeholder="Tabelle durchsuchen..."
                        suffix={<SearchOutlined/>}
                        allowClear
                        onChange={
                            (e) => {
                                let searchVal = e.target.value.toLowerCase()
                                if (searchVal.length === 0) {
                                    // emtpy search -> fill original data
                                    setFilteredData(apiData);
                                } else {
                                    // filter original data by search string
                                    setFilteredData(apiData.filter((e) => {
                                            for (let key in e) { // loop over all all columns
                                                if (key === "key") {
                                                    continue
                                                }
                                                let value: string = String(e[key]);
                                                if (value.toLowerCase().includes(searchVal)) {  // ignore capitalization
                                                    return true;  // if one col contains searchString -> keep row
                                                }
                                            }
                                            return false; // else -> remove row
                                        }
                                    ));
                                }
                            }
                        }
                    />
                ) : (<></>)}
                {props.buttons ? props.buttons : <></>}
            </Space>
            }
        >
            <Table
                ref={el => {
                    if (!!el) {
                        if (window.innerHeight - el.getBoundingClientRect().bottom !== 0) {
                            //setTableHeight(tableHeight - window.innerHeight - el.getBoundingClientRect().bottom)
                        }
                    }
                }
                }
                footer={() =>
                    <div style={{color: 'gray', textAlign: 'center'}}>
                        <small>
                            {getFooterContent()}
                        </small>
                    </div>
                }
                loading={getSpinState()}
                dataSource={filteredData}
                columns={columns}
                onChange={onChange}
                scroll={{y: Math.max(tableHeight, 240)}}
                size="small"
                pagination={props.pagination}
                rowClassName={() => props.clickable || props.onRow ? "cursor-pointer" : ""}
                onRow={
                    props.onRow ?
                        (record: any, rowIndex: any) => {
                            return {
                                onClick: () => props.onRow(record)
                            }
                        }
                        :
                        props.clickable ?
                            (record: any, rowIndex: any) => {
                                return {
                                    onClick: () => history.push(props.clickable === true ?
                                        (props.url + "/" + record.key) : (props.clickable + "/" + record.key))
                                }
                            }
                            :
                            () => {
                            }
                }
                {...props.tableProps}></Table>
        </Card>
    );
}

ApiTable.defaultProps = {
    searchable: false,
    pagination: false,
    clickable: false,
    size: 'small',
    reloadSignals: [],
    storeFilters: true,
}

/* currently not used
// special tables
interface EventTableProps {
    id: string,
    url: string,
    title?: ReactElement<any, any>,
    actions?: any,
    tableProps?: any,
    pagination?: boolean,
    clickable?: false | string,
    size?: any,
    buttons?: ReactElement<any, any>
    reloadSignals: boolean[],
    type: string,
    storeFilters?: boolean,
}

export function DeviceDataTable(props: EventTableProps) {
    const [apiData, setApiData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [innerColumns, setInnerColumns] = useState(undefined);
    const [state, setState] = useState(ViewState.initial);
    const [useFilter, setUseFilter] = useState(true);
    const [dateRange, setDateRange] = useState(undefined);
    const [dateLimit, setDateLimit] = useState(
        [dayjs("01.01.1970", "%DD.%MM.%YYYY"), dayjs().endOf('month')]
    );

    const [tableHeight, setTableHeight] = useState(window.innerHeight - 48 - 34 - 65 - 55 - 24 - 70);
    const dispatch = useAppDispatch();


    // resize table on change of windowSize
    window.addEventListener('resize', () =>
        setTableHeight(window.innerHeight - 48 - 34 - 65 - 55 - 24 - 70)
    )

    useEffect(() => {
        if (state === ViewState.initial) {
            return  // prevent auto reload before first manual load
        }

        if (!!dateRange) {
            //@ts-ignore
            let [start, end] = dateRange
            doFetch(
                //@ts-ignore
                dateRangeString = start.format("DD.MM.YYYY") + "-" + end.format("DD.MM.YYYY")
            );
        } else {
            doFetch("undefined");
        }
    }, [...props.reloadSignals])

    function onFetch() {
        let dateRangeString = undefined;
        if (!!dateRange) {
            //@ts-ignore
            let [start, end] = dateRange
            if ((end - start) >= 2678400000) {
                Modal.confirm({
                    title: "Diese Daten wirklich laden?",
                    content: "Es soll ein Zeitraum von über einem Monat geladen werden. Eine übermäßig große Menge an Daten kann zu sehr langen Ladezeiten führen.",
                    maskClosable: true,
                    cancelText: 'Abbrechen',
                    okText: 'Laden',
                    autoFocusButton: 'ok',
                    onOk: () => {
                        doFetch(
                            //@ts-ignore
                            dateRangeString = start.format("DD.MM.YYYY") + "-" + end.format("DD.MM.YYYY")
                        );
                        return false;
                    }
                })
            } else {
                doFetch(
                    //@ts-ignore
                    dateRangeString = start.format("DD.MM.YYYY") + "-" + end.format("DD.MM.YYYY")
                );
            }
        } else {
            Modal.confirm({
                title: "Diese Daten wirklich laden?",
                content: "Es sollen alle verfügbaren Daten geladen werden. Eine übermäßig große Menge an Daten kann zu sehr langen Ladezeiten führen.",
                maskClosable: true,
                cancelText: 'Abbrechen',
                okText: 'Laden',
                autoFocusButton: 'ok',
                onOk: () => {
                    doFetch("undefined");
                    return false;
                }
            })
        }
    }

    function doFetch(dateRangeString: string) {
        setState(ViewState.loading);
        get(props.url + "/table?filter=" + useFilter + "&range=" + dateRangeString)
            .done((response) => {
                    var tabledata = response.data.table
                    setApiData(tabledata.data);
                    setInnerColumns(tabledata.innerColumns);
                    let columns = tabledata.columns;
                    for (let column of columns) {
                        if (column.sorter) {
                            column.sorter = getSorter(column.sorter, column.dataIndex);
                        }

                        if (column.render) {
                            column.render = getRender(column.render);
                        }
                        column.onFilter = getFilter(column);
                    }
                    if (props.actions) {
                        columns = [...columns,
                            {
                                title: "",
                                render: props.actions,
                                width: 100
                            }];
                    }
                    loadFilters(columns);
                    setState(ViewState.success);
                }
            ).fail((resp) => setState(ViewState.failed))
            .fail((e) => console.error(e));
    }

    function onChange(pagination: any, filters: any, sorter: any, extra: any) {
        let table = getStoredFilters(props.id);
        if (extra['action'] === "sort") {
            table['sorter'] = [sorter['field'], sorter['order']];
            storeFilters(props.id, table);
            loadFilters(columns);
        } else if (extra['action'] === "filter") {
            table['filters'] = filters;
            storeFilters(props.id, table);
            loadFilters(columns);
        }
    }

    function loadFilters(cols: any) {
        let table = getStoredFilters(props.id);
        for (let colum of cols) {
            // @ts-ignore
            if (colum.sorter) {
                if (table['sorter'][0] === colum.dataIndex) {
                    // @ts-ignore
                    colum.sortOrder = table['sorter'][1];
                } else {
                    colum.sortOrder = null;
                }
            }
            // @ts-ignore
            if (table['filters'].hasOwnProperty(colum.dataIndex)) {
                // @ts-ignore
                colum.filteredValue = table['filters'][colum.dataIndex];
            }
        }
        setColumns(cols);
    }

    function getStoredFilters(id: string) {
        if (!window.localStorage.tables) {
            window.localStorage.tables = JSON.stringify({});
        }
        let tables = JSON.parse(window.localStorage.tables);
        if (!props.storeFilters || !tables.hasOwnProperty(id)) {
            tables[id] = {
                "sorter": [undefined, undefined],
                "filters": []
            };
        }
        return tables[id];
    }

    function storeFilters(id: string, table: {}) {
        let tables = JSON.parse(window.localStorage.tables);
        tables[id] = table;
        window.localStorage.tables = JSON.stringify(tables);
    }

    function getSpinState() {
        switch (state) {
            case ViewState.loading:
                return true;
            case ViewState.initial:
            case ViewState.success:
                return false;
            case ViewState.failed:
            default:
                return {
                    indicator: <>
                        <WarningTwoTone style={{fontSize: 48, paddingTop: 48}} twoToneColor='#ffc107'/>
                        <br/>
                        <span style={{color: '#6c757d'}}>Daten konnten nicht geladen werden!</span>
                    </>,
                }
        }
    }

    const fetchDateLimits = () => {
        get(props.url + "/dateRange")
            .done((response) => {
                //@ts-ignore
                setDateLimit([
                    dayjs(response['data'][0], "%DD.%MM.%YYYY"),
                    dayjs(response['data'][1], "%DD.%MM.%YYYY").add(1, 'day')
                ]);
            })
    }

    function getFooterContent() {
        console.debug(getStoredFilters(props.id)['filters'])
        const filter = getStoredFilters(props.id)['filters']
        // itterate through dict and check if any filter is active
        for (var key in filter) {
            if (filter[key] != null) {
                return "Es sind Filter aktiv! Bei Fehlen von Daten, bitte die Filter überprüfen."
            }
        }
        return ""
    }

    return (
        <Card
            headStyle={{backgroundColor: 'rgb(250, 250, 250)'}}
            bodyStyle={{padding: 0}}
            title={props.title}
            extra={
                <Space size={24}>
                    {props.buttons ? props.buttons : <></>}
                    EventFilter: <Switch defaultChecked={useFilter} onChange={(value) => setUseFilter(value)}/>
                    <DatePicker.RangePicker
                        onOpenChange={(open) => {
                            if (open) {
                                fetchDateLimits()
                            }
                        }}
                        format={"DD.MM.YYYY"}
                        disabledDate={(date) => date <= dateLimit[0] || dateLimit[1] <= date}
                        ranges={{
                            'Letzer Monat': [
                                dayjs().subtract(1, 'months').startOf('month'),
                                dayjs().subtract(1, 'months').endOf('month')
                            ],
                            'Dieser Monat': [
                                dayjs().startOf('month'),
                                dayjs().endOf('month')
                            ],
                        }}
                        placement={'bottomRight'}
                        onChange={
                            //@ts-ignore
                            (value) => setDateRange(value)
                        }
                    />
                    <Button onClick={onFetch} children={"Daten abrufen"}/>
                </Space>
            }
        >
            <Table
                loading={getSpinState()}
                footer={() =>
                    <div style={{color: 'gray', textAlign: 'center'}}>
                        <small>
                            {getFooterContent()}
                        </small>
                    </div>
                }
                dataSource={apiData}
                columns={columns}
                onChange={onChange}
                scroll={{y: Math.max(tableHeight, 240)}}
                size="small"
                pagination={props.pagination}
                rowClassName={() => props.clickable ? "cursor-pointer" : ""}
                locale={{
                    emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={
                        state === ViewState.initial ?
                            ("Noch keine Daten abgerufen") :
                            ("Mit diesen Filtern sind keine Daten verfügbar")
                    }/>
                }}
                onRow={
                    //todo: open drawer on click

                    props.clickable ? (
                            (record: any, rowIndex: any) => {
                                return {
                                    onClick: () => {
                                        //@ts-ignore
                                        dispatch(openDrawer({key: props.clickable, object: [props.type, record.key]}))
                                    }
                                }
                            }
                        ) :
                        (
                            () => {
                            }
                        )
                }
                expandable={!!innerColumns ? ({
                        indentSize: 0,
                        expandedRowRender: (row) => {
                            return <Card
                                type="inner"
                                title="Serien / Ebenen"
                                bodyStyle={{padding: 0, marginLeft: -25, marginTop: 8, marginBottom: 8, marginRight: 8}}
                                extra={<CustomButton
                                    mode={"add"}
                                    tooltip={"Neue Serie / Ebene hinzufügen"}
                                    onClick={() => dispatch(openDrawer({
                                        //@ts-ignore
                                        key: 'event', object: [props.type, undefined, row.key]
                                    }))}
                                />}
                            >
                                <Table
                                    columns={innerColumns}
                                    dataSource={row.innerData}
                                    pagination={false}
                                    scroll={{x: 100}}
                                    onRow={(record: any, rowIndex: any) => {
                                        return {
                                            onClick: () => {
                                                dispatch(openDrawer({
                                                    //@ts-ignore
                                                    key: 'event', object: [props.type, record.key]
                                                }))
                                            }
                                        }
                                    }}
                                />
                            </Card>;
                        }
                    }
                ) : ({})}
                {...props.tableProps}
            />
        </Card>
    );
}

DeviceDataTable.defaultProps = {
    searchable: false,
    pagination: false,
    clickable: false,
    onRow: false,
    size: 'small',
    reloadSignals: [],
    storeFilters: true,
}
*/